// src/pages/ProfilePage.js — Customer profile & settings
import React, { useState, useEffect, useCallback } from "react";
import { db } from "../firebase";
import { useAuth } from "../AuthContext";
import { useToast } from "../components/Toast";
import Navbar from "../components/Navbar";
import { US_STATES } from "../config";
import { doc, getDoc, setDoc } from "firebase/firestore";

export default function ProfilePage() {
  const { user } = useAuth();
  const toast = useToast();
  const [profile, setProfile] = useState({
    displayName: "", phone: "",
    defaultAddress: { name: "", street: "", apt: "", city: "", state: "NY", zip: "" },
    emailNotifications: true,
  });
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const snap = await getDoc(doc(db, "users", user.uid));
      if (snap.exists()) {
        const data = snap.data();
        setProfile((p) => ({
          ...p,
          displayName: data.displayName || user.displayName || "",
          phone: data.phone || "",
          defaultAddress: data.defaultAddress || p.defaultAddress,
          emailNotifications: data.emailNotifications !== false,
        }));
      } else {
        setProfile((p) => ({ ...p, displayName: user.displayName || "" }));
      }
      setLoaded(true);
    })();
  }, [user]);

  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    if (name.startsWith("addr_")) {
      const field = name.replace("addr_", "");
      setProfile((p) => ({ ...p, defaultAddress: { ...p.defaultAddress, [field]: value } }));
    } else {
      setProfile((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
    }
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await setDoc(doc(db, "users", user.uid), {
        uid: user.uid,
        email: user.email,
        displayName: profile.displayName,
        phone: profile.phone,
        defaultAddress: profile.defaultAddress,
        emailNotifications: profile.emailNotifications,
        updatedAt: Date.now(),
      }, { merge: true });
      toast.success("Profile saved!");
    } catch (err) {
      toast.error("Failed to save: " + err.message);
    }
    setSaving(false);
  };

  if (!loaded) {
    return (
      <div className="page-bg">
        <Navbar />
        <div className="container"><div className="card" style={{ padding: 60, textAlign: "center" }}><div className="spinner" /></div></div>
      </div>
    );
  }

  return (
    <div className="page-bg">
      <Navbar />
      <div className="container" style={{ maxWidth: 680 }}>
        <div className="animate-in">
          <h1 style={{ fontSize: 28, fontWeight: 800, marginBottom: 4 }}>Profile Settings</h1>
          <p style={{ color: "#64748b", fontSize: 14, marginBottom: 28 }}>Manage your account details, default return address, and notification preferences.</p>

          <form onSubmit={handleSave}>
            {/* Account info */}
            <div className="card hover-card" style={{ marginBottom: 20 }}>
              <h3 className="section-title">Account Information</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <div>
                  <label className="sf-label">Display Name</label>
                  <input className="sf-input" name="displayName" value={profile.displayName} onChange={handleChange} placeholder="John Doe" />
                </div>
                <div>
                  <label className="sf-label">Email</label>
                  <input className="sf-input" value={user?.email || ""} disabled style={{ opacity: 0.6 }} />
                  <p style={{ fontSize: 11, color: "#475569", marginTop: 4 }}>Email cannot be changed here.</p>
                </div>
                <div>
                  <label className="sf-label">Phone (optional)</label>
                  <input className="sf-input" name="phone" value={profile.phone} onChange={handleChange} placeholder="+1 (555) 123-4567" />
                </div>
              </div>
            </div>

            {/* Default return address */}
            <div className="card hover-card" style={{ marginBottom: 20 }}>
              <h3 className="section-title">Default Return Address</h3>
              <p style={{ color: "#64748b", fontSize: 13, marginBottom: 14 }}>This will auto-fill as the sender address when you create new orders.</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <div>
                  <label className="sf-label">Full Name</label>
                  <input className="sf-input" name="addr_name" value={profile.defaultAddress.name} onChange={handleChange} placeholder="John Doe" />
                </div>
                <div>
                  <label className="sf-label">Street Address</label>
                  <input className="sf-input" name="addr_street" value={profile.defaultAddress.street} onChange={handleChange} placeholder="123 Main St" />
                </div>
                <div>
                  <label className="sf-label">Apt / Suite / Unit</label>
                  <input className="sf-input" name="addr_apt" value={profile.defaultAddress.apt} onChange={handleChange} placeholder="Optional" />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 12 }} className="grid-3">
                  <div>
                    <label className="sf-label">City</label>
                    <input className="sf-input" name="addr_city" value={profile.defaultAddress.city} onChange={handleChange} placeholder="New York" />
                  </div>
                  <div>
                    <label className="sf-label">State</label>
                    <select className="sf-select" name="addr_state" value={profile.defaultAddress.state} onChange={handleChange}>
                      {US_STATES.map((s) => <option key={s.code} value={s.code}>{s.code}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="sf-label">ZIP</label>
                    <input className="sf-input" name="addr_zip" value={profile.defaultAddress.zip} onChange={handleChange} placeholder="10001" />
                  </div>
                </div>
              </div>
            </div>

            {/* Notifications */}
            <div className="card hover-card" style={{ marginBottom: 24 }}>
              <h3 className="section-title">Notifications</h3>
              <label className="checkbox-label" style={{ fontSize: 14 }}>
                <input type="checkbox" name="emailNotifications" checked={profile.emailNotifications} onChange={handleChange} />
                <span>Receive email notifications for order updates</span>
              </label>
              <p style={{ color: "#475569", fontSize: 12, marginTop: 8, marginLeft: 24 }}>
                We'll email you when quotes are sent, labels are ready, or void requests are processed.
              </p>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
